import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getOGMetadata, getTwitterCardConfig } from '../../config/seoConfig';

/**
 * Enhanced SEO Component for KeyQuest
 * Implements comprehensive SEO best practices including:
 * - Dynamic meta tags (title, description, keywords)
 * - Open Graph tags for social sharing
 * - Twitter Card tags
 * - Canonical URLs
 * - Robots meta tags (index/noindex)
 * - Structured data (JSON-LD)
 * - hreflang tags for multi-language support
 */
const SEO = ({
    title,
    description,
    keywords,
    canonical,
    image,
    type = 'website',
    author,
    publishedTime,
    modifiedTime,
    robots = 'index, follow',
    structuredData,
    hreflang,
    siteName = 'KeyQuest',
    appendSiteName = true,
    pageType,
    dynamicData = {},
    ogImageAlt,
    ogImageWidth,
    ogImageHeight,
    ogImageType,
    ogLocale,
    ogSection,
    ogTags,
}) => {
    // Get current location to force re-render on route changes
    const location = useLocation();

    // Get OG metadata based on page type
    const ogMetadata = pageType ? getOGMetadata(pageType, dynamicData) : {};

    // Build full title
    const fullTitle = title || ogMetadata.title || `${siteName}`;
    const finalTitle = !appendSiteName || fullTitle.includes(siteName) ? fullTitle : `${fullTitle} | ${siteName}`;

    // Build description
    const finalDescription = description || ogMetadata.description || '';

    // Default image if not provided
    const defaultImage = `${window.location.origin}/new_web_logo.png`;
    const ogImage = image || ogMetadata.image || defaultImage;

    // Build canonical URL
    const canonicalUrl = canonical || window.location.href.split('?')[0];

    // Get Twitter Card config
    const twitterConfig = getTwitterCardConfig(type);

    // OG Image metadata
    const finalOgImageWidth = ogImageWidth || ogMetadata.imageWidth || 1200;
    const finalOgImageHeight = ogImageHeight || ogMetadata.imageHeight || 630;
    const finalOgImageType = ogImageType || ogMetadata.imageType || 'image/png';
    const finalOgImageAlt = ogImageAlt || ogMetadata.imageAlt || finalTitle;
    const finalOgLocale = ogLocale || ogMetadata.locale || 'en_US';

    // OG Article metadata (for blog posts)
    const finalOgSection = ogSection || ogMetadata.section;
    const finalOgTags = ogTags || ogMetadata.tags;

    return (
        <Helmet key={location.pathname}>
            {/* Primary Meta Tags */}
            <title>{finalTitle}</title>
            <meta name="title" content={finalTitle} />
            {finalDescription && <meta name="description" content={finalDescription} />}
            {keywords && <meta name="keywords" content={keywords} />}
            {author && <meta name="author" content={author} />}

            {/* Canonical URL */}
            <link rel="canonical" href={canonicalUrl} />

            {/* Robots Meta Tag */}
            <meta name="robots" content={robots} />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:title" content={finalTitle} />
            {finalDescription && <meta property="og:description" content={finalDescription} />}
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:width" content={finalOgImageWidth.toString()} />
            <meta property="og:image:height" content={finalOgImageHeight.toString()} />
            <meta property="og:image:type" content={finalOgImageType} />
            <meta property="og:image:alt" content={finalOgImageAlt} />
            <meta property="og:site_name" content={siteName} />
            <meta property="og:locale" content={finalOgLocale} />
            {publishedTime && <meta property="article:published_time" content={publishedTime} />}
            {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
            {finalOgSection && <meta property="article:section" content={finalOgSection} />}
            {finalOgTags && Array.isArray(finalOgTags) && finalOgTags.map((tag, index) => (
                <meta key={index} property="article:tag" content={tag} />
            ))}

            {/* Twitter */}
            <meta name="twitter:card" content={twitterConfig.card} />
            <meta name="twitter:url" content={canonicalUrl} />
            <meta name="twitter:title" content={finalTitle} />
            {finalDescription && <meta name="twitter:description" content={finalDescription} />}
            <meta name="twitter:image" content={ogImage} />
            {twitterConfig.site && <meta name="twitter:site" content={twitterConfig.site} />}
            {twitterConfig.creator && <meta name="twitter:creator" content={twitterConfig.creator} />}
            {author && <meta name="twitter:creator" content={author} />}

            {/* hreflang tags for multi-language support */}
            {hreflang && hreflang.map((lang, index) => (
                <link
                    key={index}
                    rel="alternate"
                    hrefLang={lang.hreflang}
                    href={lang.href}
                />
            ))}

            {/* Structured Data (JSON-LD) */}
            {structuredData && (
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            )}
        </Helmet>
    );
};

SEO.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    keywords: PropTypes.string,
    canonical: PropTypes.string,
    image: PropTypes.string,
    type: PropTypes.string,
    author: PropTypes.string,
    publishedTime: PropTypes.string,
    modifiedTime: PropTypes.string,
    robots: PropTypes.string,
    structuredData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    hreflang: PropTypes.arrayOf(
        PropTypes.shape({
            hreflang: PropTypes.string.isRequired,
            href: PropTypes.string.isRequired,
        })
    ),
    siteName: PropTypes.string,
    appendSiteName: PropTypes.bool,
    pageType: PropTypes.string,
    dynamicData: PropTypes.object,
    ogImageAlt: PropTypes.string,
    ogImageWidth: PropTypes.number,
    ogImageHeight: PropTypes.number,
    ogImageType: PropTypes.string,
    ogLocale: PropTypes.string,
    ogSection: PropTypes.string,
    ogTags: PropTypes.arrayOf(PropTypes.string),
};

export default SEO;
